import { router } from '@inertiajs/react'
import type { KeyboardEvent } from 'react'
import { ChevronRight, KeyRound, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Credential } from '@/frontend/pages/Dashboard/Integrations/constants'

interface Props {
  credentials: Credential[]
  // Agent-scope: remove o vínculo com o agente (toggle_credential). Ausente no
  // modo org-level, onde a linha só lista/detalha.
  onDetach?: (credential: Credential) => void
}

/**
 * Lista de credenciais no padrão de linha do design system. Substitui o antigo
 * CredentialCard em grid, que usava o avatar-dither de agente (identidade de
 * colaborador) para representar um segredo — fora do padrão. Aqui: ícone de
 * chave em quadrado com o tom da marca/agente, nome + nº de campos, contagem de
 * uso e ações à direita. A LINHA INTEIRA é clicável (feedback do fundador) —
 * navega para o detalhe; o botão "Remover do agente" para a propagação.
 */
export function CredentialsList({ credentials, onDetach }: Props) {
  const openDetail = (credential: Credential) =>
    router.visit(`/dashboard/credentials/${credential.id}`)
  const eventStartedFromInteractiveChild = (
    event: KeyboardEvent<HTMLDivElement>,
  ) =>
    event.target instanceof HTMLElement
      && event.target !== event.currentTarget
      && Boolean(event.target.closest('button, a, input, select, textarea'))

  return (
    <ul className="overflow-hidden rounded-xl border border-border bg-card">
      {credentials.map((credential) => (
        <li key={credential.id} className="border-b border-border last:border-b-0">
          <div
            role="button"
            tabIndex={0}
            onClick={() => openDetail(credential)}
            onKeyDown={(event) => {
              if (eventStartedFromInteractiveChild(event)) return

              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openDetail(credential)
              }
            }}
            className="flex cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-agent-soft text-agent">
              <KeyRound className="size-4" strokeWidth={1.75} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-medium text-foreground" title={credential.name}>
                {credential.name}
              </p>
              <p className="truncate text-[11.5px] text-faint-foreground">
                {credential.description
                  || `${credential.field_count} ${credential.field_count === 1 ? 'campo' : 'campos'}`}
              </p>
            </div>
            <span
              className="hidden shrink-0 items-center gap-1 text-[11.5px] tabular-nums text-faint-foreground sm:flex"
              title={`${credential.worker_count} ${credential.worker_count === 1 ? 'agente usa' : 'agentes usam'} esta credencial`}
            >
              <Users className="size-3.5" strokeWidth={1.75} />
              {credential.worker_count}
            </span>
            {onDetach && (
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={(event) => {
                  event.stopPropagation()
                  onDetach(credential)
                }}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Trash2 className="size-3.5" />
                Remover do agente
              </Button>
            )}
            <ChevronRight className="size-4 shrink-0 text-faint-foreground" strokeWidth={1.75} />
          </div>
        </li>
      ))}
    </ul>
  )
}
